import { useEffect, useState } from "react";
import { useAuth } from "@/hooks/use-auth";
import { AppLayout } from "@/components/layout";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { formatCurrency, cn } from "@/lib/utils";
import { apiFetch } from "@/lib/api";
import { format } from "date-fns";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip,
  ResponsiveContainer, BarChart, Bar,
} from "recharts";
import {
  DollarSign, Star, Users, TrendingUp, CheckCircle2, XCircle, BarChart3, MessageSquare,
} from "lucide-react";

interface PerformanceSummary {
  total: number;
  completed: number;
  cancelled: number;
  completionRate: number;
  uniqueClients: number;
  totalEarned: number;
  avgPerSession: number;
  avgRating: number | null;
  totalRatings: number;
}

interface MonthEarnings {
  month: string;
  earned: number;
  sessions: number;
}

interface MonthRating {
  month: string;
  avgScore: number;
  count: number;
}

interface RecentRating {
  id: string;
  score: number;
  comment: string | null;
  createdAt: string;
  studentName: string;
}

interface PerformanceData {
  summary: PerformanceSummary;
  byMonth: MonthEarnings[];
  ratingsByMonth: MonthRating[];
  recentRatings: RecentRating[];
}

function StarRow({ score }: { score: number }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map(i => (
        <Star
          key={i}
          className={cn("w-3.5 h-3.5", i <= score ? "fill-primary text-primary" : "text-white/20")}
        />
      ))}
    </div>
  );
}

export default function TrainerPerformance() {
  const { user } = useAuth();
  const [data, setData] = useState<PerformanceData | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user?.id) return;
    setLoading(true);
    apiFetch(`/trainers/${user.id}/performance`)
      .then(d => { setData(d as PerformanceData); setLoading(false); })
      .catch(e => { setError(e.message); setLoading(false); });
  }, [user?.id]);

  const s = data?.summary;
  const byMonth = data?.byMonth ?? [];
  const ratingsByMonth = data?.ratingsByMonth ?? [];
  const recentRatings = data?.recentRatings ?? [];

  const ratingDistribution = [1, 2, 3, 4, 5].map(star => ({
    star,
    count: recentRatings.filter(r => r.score === star).length,
  }));

  return (
    <AppLayout>
      <div className="flex flex-col gap-8 pb-12">
        <div>
          <h1 className="text-4xl font-display font-bold text-white mb-2">My Performance</h1>
          <p className="text-muted-foreground text-lg">Your session history, ratings, and earnings at a glance.</p>
        </div>

        {error && (
          <div className="p-4 rounded-xl bg-red-500/10 border border-red-500/20 text-red-400 text-sm">{error}</div>
        )}

        {/* KPI Cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          <Card className="glass-panel border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10"><DollarSign className="w-16 h-16 text-primary" /></div>
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Total Earned</CardDescription>
              <CardTitle className="text-3xl font-mono text-primary">
                {loading ? "..." : formatCurrency(s?.totalEarned ?? 0)}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mt-1">
                {loading ? "" : `${formatCurrency(s?.avgPerSession ?? 0)} avg / session`}
              </p>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10"><Star className="w-16 h-16 text-yellow-400" /></div>
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Avg Rating</CardDescription>
              <CardTitle className="text-3xl font-mono text-yellow-400">
                {loading ? "..." : s?.avgRating != null ? s.avgRating.toFixed(1) : "—"}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mt-1">
                {loading ? "" : `from ${s?.totalRatings ?? 0} review${(s?.totalRatings ?? 0) === 1 ? "" : "s"}`}
              </p>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10"><CheckCircle2 className="w-16 h-16 text-emerald-400" /></div>
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Completion Rate</CardDescription>
              <CardTitle className={cn("text-3xl font-mono", (s?.completionRate ?? 0) >= 80 ? "text-emerald-400" : "text-amber-400")}>
                {loading ? "..." : `${s?.completionRate ?? 0}%`}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mt-1">
                {loading ? "" : `${s?.completed ?? 0} of ${s?.total ?? 0} sessions`}
              </p>
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/5 relative overflow-hidden">
            <div className="absolute top-0 right-0 p-4 opacity-10"><Users className="w-16 h-16 text-blue-400" /></div>
            <CardHeader className="pb-2">
              <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Unique Clients</CardDescription>
              <CardTitle className="text-3xl font-mono text-blue-400">
                {loading ? "..." : s?.uniqueClients ?? 0}
              </CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground mt-1">Students trained</p>
            </CardContent>
          </Card>
        </div>

        {/* Monthly Earnings Chart */}
        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="text-xl font-display uppercase tracking-wide text-white flex items-center gap-2">
              <TrendingUp className="w-5 h-5 text-primary" />
              Earnings Over Time
            </CardTitle>
            <CardDescription>Monthly payout from completed sessions</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="h-[240px] flex items-center justify-center">
                <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
              </div>
            ) : byMonth.length === 0 ? (
              <div className="h-[240px] flex flex-col items-center justify-center gap-3 text-muted-foreground">
                <BarChart3 className="w-8 h-8 opacity-40" />
                <p>No completed sessions yet.</p>
              </div>
            ) : (
              <div className="h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <LineChart data={byMonth} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#27272e" vertical={false} />
                    <XAxis dataKey="month" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} />
                    <YAxis stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} tickFormatter={v => `$${v}`} />
                    <RechartsTooltip
                      cursor={{ stroke: "#e8c848", strokeWidth: 1, strokeDasharray: "4 4" }}
                      contentStyle={{ backgroundColor: "#13131a", borderColor: "#27272e", borderRadius: "8px", color: "#fff" }}
                      formatter={(value: number, name: string) => [
                        name === "earned" ? formatCurrency(value) : value,
                        name === "earned" ? "Payout" : "Sessions",
                      ]}
                    />
                    <Line type="monotone" dataKey="earned" stroke="#e8c848" strokeWidth={2.5}
                      dot={{ fill: "#e8c848", r: 4, strokeWidth: 0 }}
                      activeDot={{ r: 6, fill: "#e8c848" }} />
                  </LineChart>
                </ResponsiveContainer>
              </div>
            )}
          </CardContent>
        </Card>

        {/* Rating Trend + Distribution side by side */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
          <Card className="glass-panel border-white/5">
            <CardHeader>
              <CardTitle className="text-xl font-display uppercase tracking-wide text-white flex items-center gap-2">
                <Star className="w-5 h-5 text-yellow-400" />
                Rating Trend
              </CardTitle>
              <CardDescription>Average student rating per month</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-[220px] flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : ratingsByMonth.length === 0 ? (
                <div className="h-[220px] flex flex-col items-center justify-center gap-3 text-muted-foreground text-sm">
                  <Star className="w-8 h-8 opacity-30" />
                  <p>No ratings received yet.</p>
                </div>
              ) : (
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={ratingsByMonth} margin={{ top: 5, right: 10, left: -10, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272e" vertical={false} />
                      <XAxis dataKey="month" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} />
                      <YAxis domain={[0, 5]} ticks={[1, 2, 3, 4, 5]} stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} />
                      <RechartsTooltip
                        cursor={{ stroke: "#facc15", strokeWidth: 1, strokeDasharray: "4 4" }}
                        contentStyle={{ backgroundColor: "#13131a", borderColor: "#27272e", borderRadius: "8px", color: "#fff" }}
                        formatter={(value: number) => [value.toFixed(1), "Avg Rating"]}
                      />
                      <Line type="monotone" dataKey="avgScore" stroke="#facc15" strokeWidth={2.5}
                        dot={{ fill: "#facc15", r: 4, strokeWidth: 0 }}
                        activeDot={{ r: 6, fill: "#facc15" }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>

          <Card className="glass-panel border-white/5">
            <CardHeader>
              <CardTitle className="text-xl font-display uppercase tracking-wide text-white flex items-center gap-2">
                <BarChart3 className="w-5 h-5 text-blue-400" />
                Rating Distribution
              </CardTitle>
              <CardDescription>Breakdown of recent star ratings</CardDescription>
            </CardHeader>
            <CardContent>
              {loading ? (
                <div className="h-[220px] flex items-center justify-center">
                  <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
              ) : recentRatings.length === 0 ? (
                <div className="h-[220px] flex flex-col items-center justify-center gap-3 text-muted-foreground text-sm">
                  <Star className="w-8 h-8 opacity-30" />
                  <p>No ratings yet.</p>
                </div>
              ) : (
                <div className="h-[220px]">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart
                      data={ratingDistribution.slice().reverse()}
                      layout="vertical"
                      margin={{ top: 5, right: 20, left: 10, bottom: 0 }}
                    >
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272e" horizontal={false} />
                      <XAxis type="number" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false} allowDecimals={false} />
                      <YAxis type="category" dataKey="star" stroke="#a1a1aa" fontSize={11} tickLine={false} axisLine={false}
                        tickFormatter={v => `${"★".repeat(v)}`} />
                      <RechartsTooltip
                        cursor={{ fill: "#1e1e24" }}
                        contentStyle={{ backgroundColor: "#13131a", borderColor: "#27272e", borderRadius: "8px", color: "#fff" }}
                        formatter={(value: number) => [value, "Reviews"]}
                      />
                      <Bar dataKey="count" fill="#60a5fa" radius={[0, 4, 4, 0]} maxBarSize={28} />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Session Summary Strip */}
        {!loading && s && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            <Card className="glass-panel border-white/5">
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Completed</CardDescription>
                <CardTitle className="text-2xl font-mono text-emerald-400">{s.completed}</CardTitle>
              </CardHeader>
              <CardContent><p className="text-xs text-muted-foreground">Sessions fully delivered</p></CardContent>
            </Card>
            <Card className="glass-panel border-white/5">
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Cancelled</CardDescription>
                <CardTitle className="text-2xl font-mono text-red-400">{s.cancelled}</CardTitle>
              </CardHeader>
              <CardContent><p className="text-xs text-muted-foreground">By you or by student</p></CardContent>
            </Card>
            <Card className="glass-panel border-white/5">
              <CardHeader className="pb-2">
                <CardDescription className="text-muted-foreground uppercase tracking-wider font-semibold text-xs">Total Booked</CardDescription>
                <CardTitle className="text-2xl font-mono text-white">{s.total}</CardTitle>
              </CardHeader>
              <CardContent><p className="text-xs text-muted-foreground">Across all time</p></CardContent>
            </Card>
          </div>
        )}

        {/* Recent Reviews */}
        <Card className="glass-panel border-white/5">
          <CardHeader>
            <CardTitle className="text-xl font-display uppercase tracking-wide text-white flex items-center gap-2">
              <MessageSquare className="w-5 h-5 text-muted-foreground" />
              Recent Reviews
            </CardTitle>
            <CardDescription>Latest student feedback</CardDescription>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="animate-pulse space-y-3">
                {[1, 2, 3].map(i => <div key={i} className="h-16 bg-white/5 rounded-xl" />)}
              </div>
            ) : recentRatings.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12 gap-3 text-muted-foreground">
                <Star className="w-10 h-10 opacity-30" />
                <p>No reviews yet. Complete sessions to start collecting feedback.</p>
              </div>
            ) : (
              <div className="space-y-3">
                {recentRatings.map(r => (
                  <div key={r.id} className="p-4 rounded-xl bg-white/[0.03] border border-white/5 hover:bg-white/[0.05] transition-colors">
                    <div className="flex items-start justify-between gap-3">
                      <div className="flex items-center gap-3">
                        <div className="w-8 h-8 rounded-full bg-secondary flex items-center justify-center text-xs font-bold border border-white/10 shrink-0">
                          {r.studentName.split(" ").map(n => n[0]).join("").toUpperCase().slice(0, 2)}
                        </div>
                        <div>
                          <p className="text-sm font-semibold text-white">{r.studentName}</p>
                          <StarRow score={r.score} />
                        </div>
                      </div>
                      <span className="text-xs text-muted-foreground shrink-0 pt-0.5">
                        {format(new Date(r.createdAt), "MMM d, yyyy")}
                      </span>
                    </div>
                    {r.comment && (
                      <p className="mt-3 text-sm text-muted-foreground leading-relaxed border-t border-white/5 pt-3">
                        "{r.comment}"
                      </p>
                    )}
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    </AppLayout>
  );
}
